import { useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// eslint-disable-next-line no-unused-vars
import { AnimatePresence, motion } from 'framer-motion';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { useKioskAuth } from './hooks/useKioskAuth';
import { useKioskState } from './hooks/useKioskState';
import { useKioskIdleTimer } from './hooks/useKioskIdleTimer';
import { useSyncEngine } from './hooks/useSyncEngine';
import KioskIdleScreen from './components/KioskIdleScreen';
import KioskIdentifyScreen from './components/KioskIdentifyScreen';
import KioskConfirmScreen from './components/KioskConfirmScreen';
import KioskClockInOut from './components/KioskClockInOut';
import KioskSuccessScreen from './components/KioskSuccessScreen';
import KioskErrorScreen from './components/KioskErrorScreen';
import KioskSelfieCapture from './components/KioskSelfieCapture';
import KioskAdminPanel from './admin/KioskAdminPanel';
import {
  Wifi,
  WifiOff,
  Building2,
  ChevronLeft,
  CloudOff,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const IDLE_TIMEOUT_SECONDS = 30;

// ---------------------------------------------------------------------------
// Framer Motion transition presets
// ---------------------------------------------------------------------------
const SCREEN_VARIANTS = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -16 },
};

const SCREEN_TRANSITION = {
  duration: 0.3,
  ease: [0.4, 0, 0.2, 1],
};

// ---------------------------------------------------------------------------
// KioskHeader -- always visible at the top of the kiosk
// ---------------------------------------------------------------------------
function KioskHeader({ deviceInfo, isOnline, pendingCount, screen, onBack }) {
  const showBack = screen !== 'idle' && screen !== 'admin';

  return (
    <header className="flex-none flex items-center justify-between px-6 py-4 bg-white/80 backdrop-blur-sm border-b border-slate-200">
      {/* Left: back button or spacer */}
      <div className="w-20">
        {showBack && (
          <motion.button
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -8 }}
            onClick={onBack}
            className="flex items-center gap-1 text-slate-500 hover:text-slate-800 transition-colors min-h-[48px] min-w-[48px] rounded-lg active:bg-slate-100"
            aria-label="Go back"
          >
            <ChevronLeft className="w-6 h-6" />
            <span className="text-base font-medium">Back</span>
          </motion.button>
        )}
      </div>

      {/* Center: site / company name */}
      <div className="flex items-center gap-3 text-center">
        <Building2 className="w-6 h-6 text-slate-400 hidden sm:block" />
        <div>
          <h1 className="text-lg font-semibold text-slate-800 leading-tight">
            {deviceInfo?.location_name || 'Kiosk'}
          </h1>
          {deviceInfo?.company_name && (
            <p className="text-sm text-slate-500 leading-tight">
              {deviceInfo.company_name}
            </p>
          )}
        </div>
      </div>

      {/* Right: connectivity indicator + pending badge */}
      <div className="w-20 flex justify-end items-center gap-2">
        {pendingCount > 0 && (
          <span
            className="flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded-full"
            title={`${pendingCount} event(s) pending sync`}
          >
            <CloudOff className="w-3.5 h-3.5" />
            {pendingCount}
          </span>
        )}
        {isOnline ? (
          <Wifi className="w-5 h-5 text-emerald-500" aria-label="Online" />
        ) : (
          <WifiOff className="w-5 h-5 text-red-400" aria-label="Offline" />
        )}
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// FullScreenLoader -- shown while the device token is being validated
// ---------------------------------------------------------------------------
function FullScreenLoader() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col items-center justify-center gap-6 select-none">
      <LoadingSpinner size={20} />
      <p className="text-xl text-slate-500 font-medium">Connecting...</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KioskApp -- main entry point (OFFLINE-FIRST)
// ---------------------------------------------------------------------------
export default function KioskApp() {
  const { deviceToken: urlToken } = useParams();
  const navigate = useNavigate();

  // -----------------------------------------------------------------------
  // Hooks
  // -----------------------------------------------------------------------
  const kioskAuth = useKioskAuth();
  const kioskState = useKioskState();
  const syncEngine = useSyncEngine(kioskAuth.apiCall);

  const idleTimer = useKioskIdleTimer(
    kioskState.screen !== 'idle' && kioskState.screen !== 'admin' ? IDLE_TIMEOUT_SECONDS : 0,
    kioskState.goToIdle,
  );

  // Destructure stable callbacks
  const { heartbeat, clockIn, clockOut, startBreak, endBreak, verifyPin } = kioskAuth;
  const { clockInSuccess, clockOutSuccess, breakStartSuccess, breakEndSuccess, showError } = kioskState;
  const { refreshPendingCount, requestSync } = syncEngine;

  // Ref to hold the latest selfieData — avoids stale closure issues in
  // handleClockIn's useCallback, which can capture an outdated value if
  // React batches the SELFIE_COMPLETE dispatch with other state changes.
  const selfieDataRef = useRef(null);
  selfieDataRef.current = kioskState.selfieData;

  // -----------------------------------------------------------------------
  // Persist device token from URL into localStorage (first-time pairing)
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (urlToken) {
      localStorage.setItem('kiosk_device_token', urlToken);
      // Also set cookie — cookies persist across Safari <-> iOS standalone boundary
      const expires = new Date(Date.now() + 365 * 864e5).toUTCString();
      document.cookie = `kiosk_dt=${encodeURIComponent(urlToken)}; expires=${expires}; path=/kiosk; SameSite=Strict`;
    }
  }, [urlToken]);

  // -----------------------------------------------------------------------
  // Redirect to pairing page when the token is invalid and validation done
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!kioskAuth.isValidating && !kioskAuth.isValid) {
      navigate('/kiosk/pair', { replace: true });
    }
  }, [kioskAuth.isValidating, kioskAuth.isValid, navigate]);

  // -----------------------------------------------------------------------
  // Heartbeat: refresh employee data every 5 minutes (only if online)
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!kioskAuth.isValid) return;

    const id = setInterval(() => {
      if (navigator.onLine) heartbeat();
    }, HEARTBEAT_INTERVAL_MS);

    return () => clearInterval(id);
  }, [kioskAuth.isValid, heartbeat]);

  // -----------------------------------------------------------------------
  // Action handlers — SIMPLIFIED (offline-first, no branching)
  //
  // All actions write to IndexedDB and return immediately.
  // The sync engine handles pushing to the server in the background.
  // -----------------------------------------------------------------------
  const handleClockIn = useCallback(
    async (userId, method) => {
      idleTimer.pause();
      // Read from ref to guarantee the latest value (not a stale closure)
      const selfieBase64 = selfieDataRef.current || null;
      console.log('[KioskApp] handleClockIn:', { userId, method, hasSelfie: !!selfieBase64, selfieLength: selfieBase64?.length || 0 });
      const result = await clockIn(userId, method, selfieBase64);

      if (result.success) {
        clockInSuccess(result);
      } else {
        showError(result.error || 'Clock-in failed. Please try again.');
      }

      refreshPendingCount();
      requestSync();
      idleTimer.resume();
    },
    [clockIn, clockInSuccess, showError, idleTimer, refreshPendingCount, requestSync],
  );

  const handleClockOut = useCallback(
    async (userId) => {
      idleTimer.pause();
      const result = await clockOut(userId);

      if (result.success) {
        clockOutSuccess(result);
      } else {
        showError(result.error || 'Clock-out failed. Please try again.');
      }

      refreshPendingCount();
      requestSync();
      idleTimer.resume();
    },
    [clockOut, clockOutSuccess, showError, idleTimer, refreshPendingCount, requestSync],
  );

  const handleVerifyPin = useCallback(
    async (pin) => {
      idleTimer.pause();
      const result = await verifyPin(pin);
      idleTimer.resume();
      return result;
    },
    [verifyPin, idleTimer],
  );

  const handleStartBreak = useCallback(
    async (userId) => {
      idleTimer.pause();
      const result = await startBreak(userId);

      if (result.success) {
        breakStartSuccess(result);
      } else {
        showError(result.error || 'Failed to start break. Please try again.');
      }

      refreshPendingCount();
      requestSync();
      idleTimer.resume();
    },
    [startBreak, breakStartSuccess, showError, idleTimer, refreshPendingCount, requestSync],
  );

  const handleEndBreak = useCallback(
    async (userId) => {
      idleTimer.pause();
      const result = await endBreak(userId);

      if (result.success) {
        breakEndSuccess(result);
      } else {
        showError(result.error || 'Failed to end break. Please try again.');
      }

      refreshPendingCount();
      requestSync();
      idleTimer.resume();
    },
    [endBreak, breakEndSuccess, showError, idleTimer, refreshPendingCount, requestSync],
  );

  // -----------------------------------------------------------------------
  // Employee list from auth hook (loaded from IndexedDB)
  // -----------------------------------------------------------------------
  const employees = kioskAuth.employees;

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  // Still validating the device token
  if (kioskAuth.isValidating) {
    return <FullScreenLoader />;
  }

  // If not valid, the useEffect above will redirect
  if (!kioskAuth.isValid) {
    return null;
  }

  // Shared props that every screen may need
  const screenProps = {
    employees,
    deviceInfo: kioskAuth.deviceInfo,
    selectedUser: kioskState.selectedUser,
    clockedIn: kioskState.clockedIn,
    onBreak: kioskState.onBreak,
    activeBreak: kioskState.activeBreak,
    identifyMethod: kioskState.identifyMethod,
    successData: kioskState.successData,
    errorMessage: kioskState.errorMessage,
    isOnline: syncEngine.isOnline,
    // State transitions
    goToIdentify: kioskState.goToIdentify,
    selectUser: kioskState.selectUser,
    confirmUser: kioskState.confirmUser,
    selfieComplete: kioskState.selfieComplete,
    rejectUser: kioskState.rejectUser,
    goToIdle: kioskState.goToIdle,
    goBack: kioskState.goBack,
    showError: kioskState.showError,
    goToAdmin: kioskState.goToAdmin,
    // Actions
    getOnSiteUsers: kioskAuth.getOnSiteUsers,
    onClockIn: handleClockIn,
    onClockOut: handleClockOut,
    onStartBreak: handleStartBreak,
    onEndBreak: handleEndBreak,
    onVerifyPin: handleVerifyPin,
    // Idle timer
    resetIdleTimer: idleTimer.reset,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col overflow-hidden select-none">
      {/* Header -- always visible (hidden in admin mode) */}
      {kioskState.screen !== 'admin' && (
        <KioskHeader
          deviceInfo={kioskAuth.deviceInfo}
          isOnline={syncEngine.isOnline}
          pendingCount={syncEngine.pendingCount}
          screen={kioskState.screen}
          onBack={kioskState.goBack}
        />
      )}

      {/* Screen content */}
      <main className="flex-1 flex flex-col relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={kioskState.screen}
            variants={SCREEN_VARIANTS}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={SCREEN_TRANSITION}
            className="flex-1 flex flex-col"
          >
            {kioskState.screen === 'idle' && (
              <KioskIdleScreen
                {...screenProps}
              />
            )}

            {kioskState.screen === 'identify' && (
              <KioskIdentifyScreen {...screenProps} />
            )}

            {kioskState.screen === 'confirm' && (
              <KioskConfirmScreen {...screenProps} />
            )}

            {kioskState.screen === 'selfie' && (
              <KioskSelfieCapture {...screenProps} />
            )}

            {kioskState.screen === 'action' && (
              <KioskClockInOut {...screenProps} />
            )}

            {kioskState.screen === 'success' && (
              <KioskSuccessScreen {...screenProps} />
            )}

            {kioskState.screen === 'error' && (
              <KioskErrorScreen {...screenProps} />
            )}

            {kioskState.screen === 'admin' && (
              <KioskAdminPanel
                syncEngine={syncEngine}
                deviceInfo={kioskAuth.deviceInfo}
                onClose={kioskState.leaveAdmin}
                clearDevice={kioskAuth.clearDevice}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Idle timer indicator -- shown on non-idle, non-admin screens */}
      {kioskState.screen !== 'idle' && kioskState.screen !== 'admin' && idleTimer.isActive && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          className="flex-none px-6 py-3 bg-white/60 backdrop-blur-sm border-t border-slate-200 text-center"
        >
          <p className="text-sm text-slate-400">
            Returning to home in{' '}
            <span className="font-semibold text-slate-600 tabular-nums">
              {idleTimer.remainingSeconds}s
            </span>
          </p>
        </motion.div>
      )}

      {/* Offline banner */}
      <AnimatePresence>
        {!syncEngine.isOnline && kioskState.screen !== 'admin' && (
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-0 inset-x-0 bg-amber-500 text-white text-center py-3 px-4 text-base font-medium z-50"
          >
            <span className="flex items-center justify-center gap-2">
              <WifiOff className="w-5 h-5" />
              Offline mode -- events are stored locally
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
